import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import { LinkProps } from '@unlikelystudio/react-abstract-components'
import Slider from '@unlikelystudio/react-slider'

import BannerImage from '~/components/UI/BannerImage'
import {
  BigLayoutIcon,
  MediumLayoutIcon,
  MonoLayoutIcon,
  SmallLayoutIcon,
} from '~/components/UI/Icons'
import { ImageProps } from '~/components/UI/Image'
import InlineCta from '~/components/UI/InlineCta'
import LayoutSwitcher from '~/components/UI/LayoutSwitcher'
import RichText, { RichTextBlock } from '~/components/UI/RichText'

import {
  LAYOUT,
  useLayoutSwitcherProvider,
} from '~/providers/LayoutSwitcherProvider'
import { usePagination } from '~/providers/PaginationProvider'
import { useStyle } from '~/providers/StyleProvider'

import useBreakpoint from '~/hooks/useBreakpoint'

import { LABEL } from '~/data/dictionary'

import css from './styles.module.scss'
import { GLOBAL_BREAKPOINTS } from '~/@types/breakpoints'

const cx = classnames.bind(css)

export type HeroCategoryProps = {
  className?: string
  title?: string
  subNavigation?: LinkProps[]
  enableBannerHero?: boolean
  image?: ImageProps
  mobileImage?: ImageProps
  leftParagraph?: string
  rightParagraph?: string
  categorySubtitle?: RichTextBlock
}

function HeroCategory({
  className,
  title,
  subNavigation,
  enableBannerHero,
  image,
  mobileImage,
  categorySubtitle,
  leftParagraph,
  rightParagraph,
}: HeroCategoryProps) {
  const { step: currentStep, setStep: setCurrentStep } =
    useLayoutSwitcherProvider()
  const isMobile = useBreakpoint(GLOBAL_BREAKPOINTS.LG)
  const customSliderRef = useRef()
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })
  const titleStyle = useStyle({ textPreset: GlobalTextPreset.Title40SangBleu })
  const { setCurrentPage } = usePagination()
  const router = useRouter()

  const earringRegex = new RegExp(/boucles-doreilles|earrings/, 'g')
  const shouldRenderSubtitle = earringRegex.test(router?.query?.uid as string)

  const subtitleStyle = useStyle({
    textPreset: GlobalTextPreset.Text12SangBleu,
    textStyling: GlobalTextStyling.Italic,
    color: GlobalThemeColors.Gray,
  })

  useEffect(() => {
    if (isMobile && currentStep === LAYOUT.SMALL) {
      setCurrentStep(LAYOUT.MEDIUM)
    }
  }, [isMobile])

  useEffect(() => {
    // When changing page, reset layout
    return () => {
      setCurrentStep(LAYOUT.MEDIUM)
    }
  }, [])

  return (
    <div
      className={cx(css.HeroCategory, className, {
        withBanner: enableBannerHero,
      })}
      ref={customSliderRef}>
      {enableBannerHero && (
        <div className={cx(css.bannerHero)}>
          {(image || mobileImage) && (
            <BannerImage
              className={cx(css.bannerImage)}
              image={image}
              mobileImage={mobileImage}
            />
          )}
          <div className={cx(css.titleRow, gridStyle)}>
            {title && <h1 className={cx(css.title, titleStyle)}>{title}</h1>}
            {(leftParagraph || rightParagraph) && (
              <div className={cx(css.paragraphs)}>
                {leftParagraph && (
                  <p className={cx(css.paragraph, css.leftParagraph)}>
                    {leftParagraph}
                  </p>
                )}
                {rightParagraph && (
                  <p className={cx(css.paragraph, css.rightParagraph)}>
                    {rightParagraph}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div className={cx(css.wrapper, gridStyle)}>
        <div className={css.left}>
          {(subNavigation?.length > 0 || !enableBannerHero) && (
            <>
              <div className={css.title}>
                {title &&
                  (enableBannerHero ? (
                    <h5 className={cx(css.title, titleStyle)}>{LABEL.ALL}</h5>
                  ) : (
                    <h1 className={cx(css.title, titleStyle)}>{title}</h1>
                  ))}
              </div>

              {subNavigation?.length > 0 && (
                <div className={cx(css.subcategories, css?.hideOnSmallScreen)}>
                  <div className={css.content}>
                    {subNavigation?.map((subcategory, index) => {
                      return (
                        <InlineCta
                          {...subcategory}
                          textPreset={GlobalTextPreset.Cta12Grotesk}
                          textStyling={GlobalTextStyling.UpperCase}
                          key={`subcategory_item_${index}`}
                          lineProps={{ initialLineState: 'none' }}
                          className={css.subcategoryLink}
                        />
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className={css.right}>
          <LayoutSwitcher
            className={cx(css.layoutSwitcher, css.hideOnSmallScreen)}
            currentStep={currentStep}
            onClickCallback={(step) => {
              setCurrentStep(step)
              // Reset current page to 1 on layout update
              setCurrentPage(1)
            }}
            steps={[
              {
                icon: <BigLayoutIcon className={css.icon} />,
                className: css.layoutSwitcherItem,
                activeClassName: css.isActive,
                name: LAYOUT.BIG,
              },
              {
                icon: <MediumLayoutIcon className={css.icon} />,
                className: css.layoutSwitcherItem,
                activeClassName: css.isActive,
                name: LAYOUT.MEDIUM,
              },
              {
                icon: <SmallLayoutIcon className={css.icon} />,
                className: css.layoutSwitcherItem,
                activeClassName: css.isActive,
                name: LAYOUT.SMALL,
              },
            ]}
          />
          <LayoutSwitcher
            className={cx(
              css.layoutSwitcher,
              css.hideOnLargeScreen,
              css.smallScreen,
            )}
            currentStep={currentStep}
            onClickCallback={(step) => {
              setCurrentStep(step)
              // Reset current page to 1 on layout update
              setCurrentPage(1)
            }}
            steps={[
              {
                icon: <MonoLayoutIcon className={css.icon} />,
                className: css.layoutSwitcherItem,
                activeClassName: css.isActive,
                name: LAYOUT.BIG,
              },
              {
                icon: <BigLayoutIcon className={css.icon} />,
                className: css.layoutSwitcherItem,
                activeClassName: css.isActive,
                name: LAYOUT.MEDIUM,
              },
            ]}
          />
        </div>
        {shouldRenderSubtitle ? (
          <RichText
            render={categorySubtitle}
            className={cx(subtitleStyle, css.categorySubtitle)}
          />
        ) : null}
        {subNavigation?.length > 0 && (
          <Slider
            className={cx(
              css.slider,
              css.mobileSubNavigation,
              css?.hideOnLargeScreen,
            )}
            customSliderRef={customSliderRef}
            snap>
            {subNavigation?.map((subcategory, index) => {
              return (
                <InlineCta
                  {...subcategory}
                  key={`subcategory_item_${index}`}
                  lineProps={{ initialLineState: 'none' }}
                  className={css.subcategoryLink}
                />
              )
            })}
          </Slider>
        )}
      </div>
    </div>
  )
}

HeroCategory.defaultProps = {}

export default HeroCategory
